import React from "react";
import { Box, Button, Chip, Container, Grid, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";

const highlights = [
  {
    title: "Shoot in seconds",
    body: "Tap to capture frames, or hold for rapid bursts. Keep everything light, quick, and repeatable.",
  },
  {
    title: "Ghost image (onion skin)",
    body: "Overlay the previous frame as a transparent guide so your subject stays perfectly aligned.",
  },
  {
    title: "Sound-first editing",
    body: "Drop in audio, nudge frames to beats, and lock in timing for playful stop-motion rhythm.",
  },
];

const steps = [
  {
    title: "Stage",
    body: "Set your scene with simple markers, guides, and a locked frame.",
  },
  {
    title: "Capture",
    body: "Use the ghost image slider to match positions precisely.",
  },
  {
    title: "Sound",
    body: "Layer narration or music, then sync the action to the waveform.",
  },
  {
    title: "Export",
    body: "Generate a loop, story, or clip in seconds. Share instantly.",
  },
];

export default function LifelapseFeature() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 20% 10%, rgba(255, 221, 168, 0.7), rgba(255, 239, 221, 0.2) 45%), linear-gradient(135deg, #101418, #1f2a2f 45%, #0f1b1e 100%)",
        color: "var(--ink)",
        fontFamily: '"Avenir Next", "Avenir", "Gill Sans", "Trebuchet MS", sans-serif',
        "--ink": "#f8f5f1",
        "--accent": "#f2b05e",
        "--accent-2": "#88d4c1",
        "--panel": "rgba(12, 18, 20, 0.72)",
        position: "relative",
        overflow: "hidden",
        pb: { xs: 8, md: 12 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "-20% 40% auto -10%",
          height: "480px",
          background: "radial-gradient(circle, rgba(136, 212, 193, 0.35), transparent 70%)",
          filter: "blur(0px)",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: "-10%",
          top: "15%",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(242, 176, 94, 0.4), transparent 70%)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, pt: { xs: 8, md: 12 } }}>
        <Stack spacing={6}>
          <Stack spacing={3}>
            <Chip
              label="Stop-motion studio"
              sx={{
                alignSelf: "flex-start",
                background: "rgba(255,255,255,0.08)",
                color: "var(--ink)",
                border: "1px solid rgba(255,255,255,0.12)",
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.05,
                maxWidth: 720,
              }}
            >
              Lifelapse-style stop motion, built for fast, magical takes.
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(248,245,241,0.75)", maxWidth: 680 }}>
              Capture frame-by-frame, align with a ghost image overlay, and add sound without
              breaking your flow. Simple tools, cinematic results.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/lifelapse/new"
                sx={{
                  background: "linear-gradient(120deg, #f2b05e, #f28b82)",
                  color: "#1a1a1a",
                  fontWeight: 800,
                  borderRadius: 3,
                  px: 4,
                }}
              >
                Start a new scene
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={3}>
            {highlights.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box
                  sx={{
                    height: "100%",
                    background: "var(--panel)",
                    borderRadius: 4,
                    border: "1px solid rgba(255,255,255,0.08)",
                    p: 3,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "rgba(248,245,241,0.72)" }}>
                    {item.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Ghost image guidance
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(248,245,241,0.72)" }}>
                  Onion skinning lets you see a transparent reference of the previous frame,
                  so your subject lands exactly where it should. Slide the opacity to match
                  tiny moves, big jumps, or subtle pauses.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 1,
                    color: "rgba(248,245,241,0.8)",
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)" }} />
                  <Typography variant="body2">Instant alignment for smooth motion.</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    color: "rgba(248,245,241,0.8)",
                  }}
                >
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent-2)" }}
                  />
                  <Typography variant="body2">Perfect for clay, paper, and tabletop builds.</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 280, md: 360 },
                  borderRadius: 6,
                  background: "linear-gradient(145deg, #0f1b1e, #1c2a2f)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  overflow: "hidden",
                  "@keyframes floatFrame": {
                    "0%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-8px)" },
                    "100%": { transform: "translateY(0px)" },
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: "18% 18% 18% 18%",
                    borderRadius: 4,
                    border: "1px solid rgba(255,255,255,0.15)",
                    background: "rgba(255,255,255,0.04)",
                    animation: "floatFrame 6s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: "22% 22% 22% 22%",
                    borderRadius: 4,
                    background: "rgba(136, 212, 193, 0.2)",
                    border: "1px solid rgba(136, 212, 193, 0.4)",
                    mixBlendMode: "screen",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: "26% 26% 26% 26%",
                    borderRadius: 4,
                    background: "rgba(242, 176, 94, 0.3)",
                    border: "1px dashed rgba(242, 176, 94, 0.6)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 24,
                    left: 24,
                    right: 24,
                    height: 6,
                    borderRadius: 99,
                    background:
                      "linear-gradient(90deg, rgba(242,176,94,0.2), rgba(242,176,94,0.9), rgba(136,212,193,0.6))",
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: { xs: 240, md: 300 },
                  borderRadius: 6,
                  background: "linear-gradient(145deg, rgba(248,245,241,0.06), rgba(12,18,20,0.7))",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "grid",
                  placeItems: "center",
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 4,
                    background:
                      "repeating-linear-gradient(90deg, rgba(248,245,241,0.05) 0 12px, rgba(136,212,193,0.18) 12px 24px)",
                    position: "relative",
                    overflow: "hidden",
                    "@keyframes pulseWave": {
                      "0%": { transform: "translateX(-20%)" },
                      "100%": { transform: "translateX(20%)" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "15%",
                      borderRadius: 3,
                      background: "rgba(242,176,94,0.2)",
                      border: "1px solid rgba(242,176,94,0.5)",
                      animation: "pulseWave 4s ease-in-out infinite alternate",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "35% 18%",
                      borderRadius: 3,
                      background: "rgba(248,245,241,0.15)",
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Audio that drives the cut
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(248,245,241,0.72)" }}>
                  Add music, narration, or foley tracks straight into the timeline. The waveform
                  view keeps timing precise, so every frame lands on the beat.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Chip
                    label="Waveform sync"
                    sx={{
                      background: "rgba(136,212,193,0.2)",
                      color: "var(--ink)",
                      border: "1px solid rgba(136,212,193,0.5)",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label="Voice + music"
                    sx={{
                      background: "rgba(242,176,94,0.2)",
                      color: "var(--ink)",
                      border: "1px solid rgba(242,176,94,0.5)",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Box
            sx={{
              background: "rgba(255,255,255,0.05)",
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.08)",
              p: { xs: 3, md: 4 },
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
              A simple stop-motion flow
            </Typography>
            <Grid container spacing={3}>
              {steps.map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={step.title}>
                  <Stack spacing={1.5}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(248,245,241,0.6)", letterSpacing: 1.8 }}
                    >
                      Step {index + 1}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(248,245,241,0.7)" }}>
                      {step.body}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            sx={{
              background: "linear-gradient(120deg, rgba(136,212,193,0.2), rgba(242,176,94,0.15))",
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.12)",
              p: { xs: 4, md: 5 },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to build your next tiny epic?
            </Typography>
            <Typography variant="body1" sx={{ color: "rgba(248,245,241,0.78)", mb: 3 }}>
              Capture the magic with ghost frames, crisp audio, and a workflow that stays out of
              the way.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                background: "linear-gradient(120deg, #88d4c1, #f2b05e)",
                color: "#0f1b1e",
                fontWeight: 800,
                borderRadius: 3,
                px: 5,
              }}
            >
              Launch Lifelapse mode
            </Button>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
